import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import type { TaskAssignee } from '../api/tasks';
import {
  createTaskSubtask,
  deleteTaskSubtask,
  updateTaskSubtask,
  type TaskSubtaskRow,
} from '../api/tasks';
import './TaskSubtasks.css';

export interface TaskSubtasksEditorProps {
  taskId: number;
  viewerUserId: number;
  isVoid: boolean;
  assignees: TaskAssignee[];
  subtasks: TaskSubtaskRow[];
  setSubtasks: React.Dispatch<React.SetStateAction<TaskSubtaskRow[]>>;
  reloadSubtasks: (opts?: { silent?: boolean }) => Promise<void>;
  onActivityRefresh: () => void;
}

const TaskSubtasksEditor: React.FC<TaskSubtasksEditorProps> = ({
  taskId,
  viewerUserId,
  isVoid,
  assignees,
  subtasks,
  setSubtasks,
  reloadSubtasks,
  onActivityRefresh,
}) => {
  const [error, setError] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newAssigneeUserId, setNewAssigneeUserId] = useState<string>('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const handleAdd = async () => {
    const title = newTitle.trim();
    if (!title || isVoid) return;
    let optionalAssignee: { assignee_user_id: number } | Record<string, never> = {};
    if (newAssigneeUserId !== '') {
      const uid = parseInt(newAssigneeUserId, 10);
      if (!Number.isInteger(uid) || uid < 1) {
        setError('Invalid assignee.');
        return;
      }
      optionalAssignee = { assignee_user_id: uid };
    }
    setAdding(true);
    setError('');
    try {
      const created = await createTaskSubtask(taskId, {
        user_id: viewerUserId,
        title,
        ...optionalAssignee,
        sort_order: subtasks.length,
      });
      setSubtasks((prev) => [...prev, created]);
      setNewTitle('');
      setNewAssigneeUserId('');
      onActivityRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add subtask.');
    } finally {
      setAdding(false);
    }
  };

  const patchSubtask = async (
    subtaskId: number,
    body: Omit<Parameters<typeof updateTaskSubtask>[2], 'user_id'>
  ) => {
    setBusyId(subtaskId);
    setError('');
    try {
      const updated = await updateTaskSubtask(taskId, subtaskId, { user_id: viewerUserId, ...body });
      setSubtasks((prev) => prev.map((s) => (s.id === subtaskId ? updated : s)));
      onActivityRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update subtask.');
      void reloadSubtasks({ silent: true });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (sub: TaskSubtaskRow) => {
    if (isVoid) return;
    if (!window.confirm(`Delete subtask “${sub.title}”?`)) return;
    setBusyId(sub.id);
    setError('');
    try {
      await deleteTaskSubtask(taskId, sub.id, viewerUserId);
      setSubtasks((prev) => prev.filter((s) => s.id !== sub.id));
      onActivityRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete subtask.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="task-subtasks-editor">
      <p className="page-subtitle task-subtasks-editor-hint">
        Break work into steps. Only people in &ldquo;Assigned to&rdquo; above can be assigned to a subtask. Save the task
        first if you changed assignees, then pick subtask assignees.
      </p>

      {error ? (
        <p className="create-order-error task-subtasks-error" role="alert">
          {error}
        </p>
      ) : null}

      {!isVoid && (
        <div className="task-subtasks-add-row">
          <input
            type="text"
            className="form-input task-subtasks-add-title"
            placeholder="New subtask title…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            disabled={adding}
            maxLength={500}
            aria-label="New subtask title"
          />
          <select
            className="form-select task-subtasks-add-assignee"
            value={newAssigneeUserId}
            onChange={(e) => setNewAssigneeUserId(e.target.value)}
            disabled={adding || assignees.length === 0}
            aria-label="Assign to"
            title={assignees.length === 0 ? 'Add people to Assigned to first' : undefined}
          >
            <option value="">Unassigned</option>
            {assignees.map((a) => (
              <option key={a.user_id} value={String(a.user_id)}>
                {a.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="task-subtasks-add-btn"
            onClick={() => void handleAdd()}
            disabled={adding || !newTitle.trim()}
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}

      {subtasks.length === 0 ? (
        <p className="task-subtasks-empty">No subtasks yet.</p>
      ) : (
        <div className="task-subtasks-table-wrap">
          <table className="task-notes-table task-subtasks-table">
            <thead>
              <tr>
                <th className="task-subtasks-col-done" scope="col">
                  Done
                </th>
                <th scope="col">Title</th>
                <th scope="col">Assignee</th>
                <th className="task-subtasks-col-actions" scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {subtasks.map((s) => (
                <tr key={s.id}>
                  <td className="task-subtasks-col-done">
                    <input
                      type="checkbox"
                      checked={s.is_completed}
                      disabled={isVoid || busyId === s.id}
                      onChange={(e) => void patchSubtask(s.id, { is_completed: e.target.checked })}
                      aria-label={`Complete: ${s.title}`}
                    />
                  </td>
                  <td className="task-subtasks-title-cell">
                    <input
                      type="text"
                      className="form-input task-subtasks-title-input"
                      value={s.title}
                      disabled={isVoid || busyId === s.id}
                      maxLength={500}
                      onFocus={(e) => {
                        e.currentTarget.dataset.persistTitle = s.title;
                      }}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSubtasks((prev) => prev.map((x) => (x.id === s.id ? { ...x, title: v } : x)));
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (!v) {
                          void reloadSubtasks({ silent: true });
                          return;
                        }
                        const atFocus = e.currentTarget.dataset.persistTitle ?? '';
                        if (v !== atFocus.trim()) {
                          void patchSubtask(s.id, { title: v });
                        }
                      }}
                      aria-label="Subtask title"
                    />
                  </td>
                  <td>
                    <select
                      className="form-select task-subtasks-assignee-select"
                      value={s.assignee_user_id != null ? String(s.assignee_user_id) : ''}
                      disabled={isVoid || busyId === s.id}
                      onChange={(e) => {
                        const v = e.target.value;
                        void patchSubtask(s.id, {
                          assignee_user_id: v === '' ? null : parseInt(v, 10),
                        });
                      }}
                      aria-label="Subtask assignee"
                    >
                      <option value="">Unassigned</option>
                      {s.assignee_user_id != null &&
                      !assignees.some((a) => a.user_id === s.assignee_user_id) ? (
                        <option value={String(s.assignee_user_id)}>
                          {s.assignee_name ?? `User #${s.assignee_user_id}`}
                        </option>
                      ) : null}
                      {assignees.map((a) => (
                        <option key={a.user_id} value={String(a.user_id)}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="task-subtasks-col-actions">
                    <button
                      type="button"
                      className="project-checklist-icon-btn task-subtasks-delete"
                      title="Delete subtask"
                      aria-label={`Delete ${s.title}`}
                      disabled={isVoid || busyId === s.id}
                      onClick={() => void handleDelete(s)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaskSubtasksEditor;
